import { useEffect, useState } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PositionList } from '@/components/positions/PositionList';
import { usePositionsStore } from '@/store/positionsStore';
import type { PageProps } from '@/types/page';
import { Link } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { PlusIcon } from 'lucide-react';

export default function PositionsPage({ nav = true, footer = true }: PageProps) {
    const { positions, isLoading, loadPositions } = usePositionsStore();
    const { teams, getUserTeamRole } = useAuth();
    const [teamId, setTeamId] = useState<string | undefined>();

    // Determine if user can create positions
    const canCreatePosition = () => {
        if (!teams || teams.length === 0) return true; // individual user
        if (!teamId) return true; // personal positions
        const role = getUserTeamRole(teamId);
        return role === 'owner' || role === 'admin';
    };

    // Load positions on mount and when team selection changes
    useEffect(() => {
        loadPositions(teamId);
    }, [teamId, loadPositions]);

    // Set default team if user has teams and no team selected
    useEffect(() => {
        if (teams && teams.length > 0 && !teamId) {
            setTeamId(teams[0].team.id);
        }
    }, [teams, teamId]);

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-10 mx-auto max-w-6xl w-full space-y-8">
                <div className="flex items-end justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Positions</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Create and manage job positions for your interview sessions.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {canCreatePosition() && (
                            <Link to="/app/positions/create">
                                <Button>
                                    <PlusIcon className="h-4 w-4 mr-2" />
                                    Create Position
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>

                <div className="space-y-6">
                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                        </div>
                    ) : (
                        <PositionList
                            positions={positions}
                            teamId={teamId}
                            canManagePosition={canCreatePosition()}
                        />
                    )}
                </div>
            </div>
        </PageLayout>
    );
}
